/* eslint-disable max-lines-per-function */
import { inject, Injectable } from '@angular/core';
import { ValidationErrors, ValidatorFn } from '@angular/forms';

import { Ability } from '@core/abilities-types';
import { User } from '@core/account-types';
import { EmailSenderKindEnum } from '@core/automated-actions-types';
import { AssignToReferences, AutomationActionType } from '@core/workflow-automations-types';
import {
  AutomationActionError,
  AutomationActionForm,
  AutomationsJobItem,
} from '@recruitee/workflow-automations-types';

import { ActionVisibilityHelpers } from './action-visibility.helpers';
import { AssignActionDataFacade } from './assign-action-data.facade';

@Injectable()
export class AutomationsValidationService {
  private readonly assignActionDataFacade = inject(AssignActionDataFacade);
  private readonly actionVisibilityHelpers = inject(ActionVisibilityHelpers);

  public automatedActionValidator(
    jobData?: AutomationsJobItem | null,
    references?: AssignToReferences,
  ): ValidatorFn {
    const recruiter = jobData?.recruiter;
    const hiringManager = jobData?.hiringManager;

    return (control: AutomationActionForm): ValidationErrors | null => {
      const actionForm = control.getRawValue();

      switch (actionForm.type) {
        case AutomationActionType.CandidateAddTags:
        case AutomationActionType.JobAddTags:
          const tags = actionForm.data?.tags;
          if (!tags?.length) {
            return { [AutomationActionError.EmptyTags]: true };
          }
          break;
        case AutomationActionType.CandidateAddSourceTags: {
          const tags = actionForm.data?.sourceTags;
          if (!tags?.length) {
            return { [AutomationActionError.EmptyTags]: true };
          }
          break;
        }
        case AutomationActionType.CandidateAddFollowers:
          const { followerIds, addHiringManager, addRecruiter } = actionForm.data || {};
          if (!followerIds?.length && !addRecruiter && !addHiringManager) {
            return { [AutomationActionError.EmptyFollowers]: true };
          }

          const isRoleValid =
            (followerIds?.length || addRecruiter || addHiringManager) &&
            this.hasUserAccessToJob(!!addRecruiter, recruiter, jobData?.id || null) &&
            this.hasUserAccessToJob(!!addHiringManager, hiringManager, jobData?.id || null);
          if (!isRoleValid) {
            return { [AutomationActionError.UserRoleError]: true };
          }
          break;
        case AutomationActionType.CandidateAddNote:
        case AutomationActionType.JobAddNote:
          if (!this.actionVisibilityHelpers.isActionVisible(actionForm.data.visibility)) break;

          if (!actionForm.data?.bodyJson) {
            return { [AutomationActionError.EmptyNote]: true };
          }
          break;
        case AutomationActionType.CandidateAddTask: {
          if (!actionForm.data?.title) {
            return { [AutomationActionError.EmptyTask]: true };
          }

          const adminsAddRecruiter = actionForm.data?.adminIdsAddRecruiter;
          const adminsAddHiringManager = actionForm.data?.adminIdsAddHiringManager;
          const isRoleValid =
            this.hasUserAccessToJob(!!adminsAddRecruiter, recruiter, jobData?.id || null) &&
            this.hasUserAccessToJob(!!adminsAddHiringManager, hiringManager, jobData?.id || null);

          if (!isRoleValid) {
            return { [AutomationActionError.UserRoleError]: true };
          }
          break;
        }
        case AutomationActionType.CandidateEvaluationRequest: {
          const { interviewTemplateId, adminIds, adminIdsAddHiringManager, adminIdsAddRecruiter } =
            actionForm.data || {};
          const errors: Partial<Record<AutomationActionError, true>> = {};
          if (!interviewTemplateId) {
            errors[AutomationActionError.EmptyEvaluationForm] = true;
          }
          if (!adminIds?.length && !adminIdsAddRecruiter && !adminIdsAddHiringManager) {
            errors[AutomationActionError.EmptyEvaluationMembers] = true;
          }

          const isRoleValid =
            this.hasUserAccessToJob(!!adminIdsAddRecruiter, recruiter, jobData?.id || null) &&
            this.hasUserAccessToJob(!!adminIdsAddHiringManager, hiringManager, jobData?.id || null);

          if (!isRoleValid) {
            errors[AutomationActionError.UserRoleError] = true;
          }
          if (Object.keys(errors).length) {
            return errors;
          }
          break;
        }
        case AutomationActionType.CandidateAssignTo: {
          const jobIds = actionForm.data.offerIds || [];
          const talentPoolIds = actionForm.data.talentPoolIds || [];
          const availableJobs = this.assignActionDataFacade.getAvailableJobs(
            references?.offers || [],
          );
          const availableTalentPools = this.assignActionDataFacade.getAvailableTalentPools(
            references?.talentPools || [],
          );
          const validJobIds = jobIds.filter((id: number) =>
            availableJobs.some(job => job.id === id),
          );
          const validTalentPoolIds = talentPoolIds.filter((id: number) =>
            availableTalentPools.some(tp => tp.id === id),
          );

          if (!validJobIds?.length && !validTalentPoolIds?.length) {
            return { [AutomationActionError.EmptyAssignTo]: true };
          }
          break;
        }
        case AutomationActionType.CandidateUnassignFromJob: {
          const jobIds = actionForm.data.offerIds || [];
          const talentPoolIds = actionForm.data.talentPoolIds || [];
          const includeSelf = actionForm.data.includeSelf || false;
          const availableJobs = this.assignActionDataFacade.getAvailableJobs(
            references?.offers || [],
          );
          const availableTalentPools = this.assignActionDataFacade.getAvailableTalentPools(
            references?.talentPools || [],
          );
          const validJobIds = jobIds.filter((id: number) =>
            availableJobs.some(job => job.id === id),
          );
          const validTalentPoolIds = talentPoolIds.filter((id: number) =>
            availableTalentPools.some(tp => tp.id === id),
          );

          if (!validJobIds?.length && !validTalentPoolIds?.length && !includeSelf) {
            return { [AutomationActionError.EmptyAssignTo]: true };
          }
          break;
        }
        case AutomationActionType.CandidateSendEmail: {
          const isSubjectValid = !!actionForm.data.subject?.length;
          const isBodyHtmlValid = !!actionForm.data.bodyHtml?.length;

          if (!this.actionVisibilityHelpers.isActionVisible(actionForm.data.visibility)) break;

          if (!isSubjectValid || !isBodyHtmlValid) {
            return {
              [AutomationActionError.EmptyEmailSubject]: !isSubjectValid,
              [AutomationActionError.EmptyEmailBodyHtml]: !isBodyHtmlValid,
            };
          }

          const isRecruiterAssigned =
            actionForm.data.fromKind === EmailSenderKindEnum.Recruiter ||
            actionForm.data.toRecruiter ||
            actionForm.data.ccRecruiter ||
            actionForm.data.bccRecruiter;
          const hasRecruiterAccessToJob = this.hasUserAccessToJob(
            isRecruiterAssigned,
            recruiter,
            jobData?.id || null,
          );
          const hasRecruiterSendEmailAbility = this.hasUserSendEmailAbility(
            isRecruiterAssigned,
            recruiter,
            jobData?.id || null,
          );
          const isRecruiterValid = hasRecruiterAccessToJob && hasRecruiterSendEmailAbility;
          const isHiringManagerAssigned =
            actionForm.data.fromKind === EmailSenderKindEnum.HiringManager ||
            actionForm.data.toHiringManager ||
            actionForm.data.ccHiringManager ||
            actionForm.data.bccHiringManager;
          const hasHiringManagerAccessToJob = this.hasUserAccessToJob(
            isHiringManagerAssigned,
            hiringManager,
            jobData?.id || null,
          );
          const hasHiringManagerSendEmailAbility = this.hasUserSendEmailAbility(
            isHiringManagerAssigned,
            hiringManager,
            jobData?.id || null,
          );
          const isHiringManagerValid =
            hasHiringManagerAccessToJob && hasHiringManagerSendEmailAbility;

          if (!isRecruiterValid || !isHiringManagerValid) {
            return {
              [AutomationActionError.UserRoleError]: true,
            };
          }
          break;
        }
        case AutomationActionType.CandidateCancelScheduledEvents:
          {
            if (actionForm.data.notifyCandidate) {
              const isSubjectValid = !!actionForm.data.subject.length;
              const isBodyHtmlValid = !!actionForm.data.bodyHtml.length;

              if (!isSubjectValid || !isBodyHtmlValid) {
                return {
                  [AutomationActionError.EmptyEmailSubject]: !isSubjectValid,
                  [AutomationActionError.EmptyEmailBodyHtml]: !isBodyHtmlValid,
                };
              }

              const isRecruiterAssigned =
                actionForm.data.fromKind === EmailSenderKindEnum.Recruiter;
              const hasRecruiterAccessToJob = this.hasUserAccessToJob(
                isRecruiterAssigned,
                recruiter,
                jobData?.id || null,
              );
              const hasRecruiterSendEmailAbility = this.hasUserSendEmailAbility(
                isRecruiterAssigned,
                recruiter,
                jobData?.id || null,
              );

              const isRecruiterValid = hasRecruiterAccessToJob && hasRecruiterSendEmailAbility;
              const isHiringManagerAssigned =
                actionForm.data.fromKind === EmailSenderKindEnum.HiringManager;
              const hasHiringManagerAccessToJob = this.hasUserAccessToJob(
                isHiringManagerAssigned,
                hiringManager,
                jobData?.id || null,
              );
              const hasHiringManagerSendEmailAbility = this.hasUserSendEmailAbility(
                isHiringManagerAssigned,
                hiringManager,
                jobData?.id || null,
              );
              const isHiringManagerValid =
                hasHiringManagerAccessToJob && hasHiringManagerSendEmailAbility;

              if (!isRecruiterValid || !isHiringManagerValid) {
                return {
                  [AutomationActionError.UserRoleError]: true,
                };
              }
            }
          }
          break;
        case AutomationActionType.JobAddTeamMembers:
        case AutomationActionType.CandidateShare:
        case AutomationActionType.CandidateUnshare:
          {
            const { adminIds } = actionForm.data || {};
            if (!adminIds?.length) {
              return { [AutomationActionError.EmptyTeamMembers]: true };
            }
          }
          break;
        case AutomationActionType.JobChangeStatus:
          {
            const { status } = actionForm.data || {};
            if (!status) {
              return { [AutomationActionError.EmptyJobStatus]: true };
            }
          }
          break;
        case AutomationActionType.JobSubmitToJobBoards:
          {
            const { jobBoards } = actionForm.data || {};
            if (!jobBoards?.length) {
              return { [AutomationActionError.EmptyJobBoards]: true };
            }
          }
          break;
        default:
          break;
      }

      return null;
    };
  }

  public hasUserAccessToJob(
    isUserRoleSelected: boolean,
    user: User | null = null,
    jobId: number | null = null,
  ): boolean {
    return jobId && isUserRoleSelected
      ? !!user
        ? !!(user.viewAllJobs || user.offerIds?.includes(jobId))
        : true
      : true;
  }

  public hasUserSendEmailAbility(
    isUserRoleSelected: boolean,
    user: User | null = null,
    jobId: number | null = null,
  ): boolean {
    return jobId && isUserRoleSelected
      ? !!user
        ? user.roleAbilities.includes(Ability.SendEmails)
        : true
      : true;
  }
}
